"use client";

import posthog from "posthog-js";

import { HiArrowRight, HiOutlineUserCircle } from "react-icons/hi2";

import Link from "next/link";
import { useParams, usePathname, useSearchParams } from "next/navigation";

import { Button } from "@/components/ui";

import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";

import { getPatientPortalPath, isEmbedPath } from "@/lib/embed-links";
import { withReturnTo } from "@/lib/redirect-utils";
import { cx } from "@/lib/tailwind-utils";

import { PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";

type PromptCopyProps = {
  className?: string;
  /** Override the default heading; useful for context-specific copy. */
  title?: string;
  description?: string;
};

type PatientPortalSignUpPromptProps = PromptCopyProps & {
  /**
   * Optional claim state. When both are provided, the prompt skips its own
   * `usePatientClaim()` subscription — pass these in from a parent that
   * already reads the claim to avoid duplicate auth listeners and PostHog
   * identify side effects.
   */
  authMode?: PatientClaimAuthMode;
  claimedPatient?: SelfSchedulePatient | null;
};

/**
 * Renders a CTA inviting the patient to create a patient-portal account.
 *
 * Only shows when the current session was established via phone-OTP and the
 * claimed Patient row isn't already linked to a portal account. Returns null
 * otherwise, so it's safe to drop into any post-auth landing surface.
 *
 * Pass `authMode` and `claimedPatient` from an existing `usePatientClaim()`
 * call when the surrounding component already reads the claim; otherwise
 * omit them and the prompt will subscribe on its own.
 */
export const PatientPortalSignUpPrompt = (
  props: PatientPortalSignUpPromptProps,
) => {
  // Picking which child to render is fine — each child consistently calls
  // its own hooks, so we don't violate the rules of hooks at this level.
  if (props.authMode !== undefined) {
    return (
      <PatientPortalSignUpPromptView
        authMode={props.authMode}
        claimedPatient={props.claimedPatient ?? null}
        className={props.className}
        title={props.title}
        description={props.description}
      />
    );
  }
  return <ConnectedPatientPortalSignUpPrompt {...props} />;
};

const ConnectedPatientPortalSignUpPrompt = (props: PromptCopyProps) => {
  const { authMode, claimedPatient } = usePatientClaim();
  return (
    <PatientPortalSignUpPromptView
      authMode={authMode}
      claimedPatient={claimedPatient}
      {...props}
    />
  );
};

type PromptViewProps = PromptCopyProps & {
  authMode: PatientClaimAuthMode;
  claimedPatient: SelfSchedulePatient | null;
};

const PatientPortalSignUpPromptView = ({
  className,
  title = "Create your patient portal account",
  description = "Manage appointments, complete forms, and message your care team — all in one place.",
  authMode,
  claimedPatient,
}: PromptViewProps) => {
  const { tenant } = useParams<{ tenant: string }>();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (authMode !== PatientClaimAuthMode.PHONE) return null;
  if (!claimedPatient) return null;
  if (claimedPatient.hasPortalAccount) return null;
  if (!tenant) return null;

  const portalSignUpPath = getPatientPortalPath(
    tenant,
    "sign-up",
    isEmbedPath(pathname),
  );
  const query = searchParams.toString();
  const returnTo = query ? `${pathname}?${query}` : pathname;
  const signUpHref = withReturnTo(
    `${portalSignUpPath}?source=prompt`,
    returnTo,
  );

  const handleClick = () => {
    posthog.capture("patient_portal_signup_prompt_clicked", {
      tenant,
      // The host pathname tells us which surface drove the click —
      // forms, self-schedule complete, shop complete, etc. Drives the
      // `portal_signup_prompt_clicks` metric's source-surface breakdown.
      host_pathname: pathname,
      patient_id: claimedPatient.id,
    });
  };

  return (
    <div
      className={cx(
        "overflow-hidden rounded-xl border border-border bg-background text-left",
        className,
      )}
    >
      <div className="flex items-start gap-3 px-4 py-4">
        <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground/5 text-foreground-muted">
          <HiOutlineUserCircle className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">
            Patient portal
          </p>
          <h3 className="text-body font-semibold leading-tight text-foreground">
            {title}
          </h3>
          <p className="text-caption text-foreground-muted">{description}</p>
        </div>
      </div>

      <div className="border-t border-border p-3">
        <Link href={signUpHref} className="block" onClick={handleClick}>
          <Button variant="primary" className="group w-full">
            <span>Sign up for the patient portal</span>
            <HiArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default PatientPortalSignUpPrompt;
